import { useId, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../../../../../bombiix-icon';
import type { FieldSetupCardProps, FieldSetupToggle, FieldSetupToggleKey } from './field-setup-card.types';
import { NewInput } from '../../../../../Components/PIM/common/NewInput';
import LibraryTitleAppearanceContext from '../../../../../Components/Controls/library-title-input.context';
import CmPanelTabs from '../../../../../Components/Controls/cm-panel-tabs';
import FieldSetupPanelContext from '../../../../../Components/Controls/cm-control-config/field-setup-panel.context';
import type { FieldSetupPanel } from '../../../../../Components/Controls/cm-control-config/field-setup-panel.context';
import { NewRadio } from '../../../../../Components/PIM/common/NewRadio';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';

/**
 * Setup card a Custom Module field opens into from the section drawer (Figma "Slide Out Controls").
 * Each field type gets the tabs Figma gives it; the Setup tab starts with Alias Name and ends with the
 * type's toggle rows when `access` is given, with the type's own setup fields in between.
 * @param props - See FieldSetupCardProps.
 * @returns The setup card.
 */
function FieldSetupCard({ fieldType, title, onTitleChange, onClose, children, width, onWidthChange, widthError, access, onAccessChange }: FieldSetupCardProps) {
  const { t } = useTranslation('custom-module-system-canvas');
  const fieldTypeLabel = t(FIELD_TYPE_LABEL_KEYS[fieldType]);
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.FIELD_TITLE_PLACEHOLDER', { fieldType: fieldTypeLabel });
  const widthPct = parseWidthPct(width);
  const sliderId = useId();
  const aliasId = useId();
  const panels = PANELS_BY_TYPE[fieldType];
  const [activePanel, setActivePanel] = useState<FieldSetupPanel>('setup');
  const shownPanel: FieldSetupPanel = panels.includes(activePanel) ? activePanel : 'setup';
  const headerKey = shownPanel === 'options' ? 'CUSTOM_MODULE.SYSTEM_CANVAS.FIELD_OPTIONS' : 'CUSTOM_MODULE.SYSTEM_CANVAS.FIELD_SETUP';
  const panelTabs = panels.map((panel) => ({ key: panel, label: t(PANEL_LABEL_KEYS[panel]) }));
  const showTitleInput = !TITLELESS_FIELD_TYPES.includes(fieldType);
  const isRequiredLocked = showTitleInput && !title.trim();
  const showSetupExtras = shownPanel === 'setup' && !!access && !!onAccessChange;

  const handleWidthChange = (event: ChangeEvent<HTMLInputElement>) => {
    onWidthChange?.(`${event.target.value}%`);
  };

  /**
   * Renders one toggle row; Required stays locked with a tooltip until the field has a title.
   * @param toggle - The toggle to render.
   * @returns The toggle row.
   */
  const renderToggle = (toggle: FieldSetupToggle) => {
    const label = t(TOGGLE_LABEL_KEYS[toggle.key]);
    if (toggle.key === 'required' && isRequiredLocked) {
      return (
        <CustomTooltip key={toggle.key} tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
          <SetupToggle label={label} checked={toggle.checked} disabled onToggle={() => {}} />
        </CustomTooltip>
      );
    }
    return <SetupToggle key={toggle.key} label={label} checked={toggle.checked} onToggle={() => onAccessChange?.({ [toggle.key]: !toggle.checked })} />;
  };

  return (
    <div className="cm-field-setup">
      <div className="cm-field-setup__card">
        <div className="cm-field-setup__header">
          <button type="button" className="bombiix-btn btn-blue cm-field-setup__save" onClick={onClose}>
            {t('COMMON.SAVE')}
          </button>
          <span className="cm-field-setup__title">{t(headerKey, { fieldType: fieldTypeLabel })}</span>
          <button type="button" className="cm-field-setup__close" aria-label={t('COMMON.CLOSE')} onClick={onClose}>
            <CloseIcon size={SETUP_CLOSE_ICON_SIZE} />
          </button>
        </div>
        <div className="cm-field-setup__body">
          {showTitleInput && <NewInput type="text" placeholder={titlePlaceholder} value={title} onChange={(event) => onTitleChange(event.target.value)} />}
          {panelTabs.length > 0 && (
            <CmPanelTabs
              className="cm-panel-tabs--filled"
              tabs={panelTabs}
              activeKey={shownPanel}
              onChange={setActivePanel}
              ariaLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.FIELD_SETUP', { fieldType: fieldTypeLabel })}
            />
          )}
          {showSetupExtras && access?.aliasName !== undefined && (
            <div className="cm-field-setup__field">
              <label className="cm-field-setup__field-label" htmlFor={aliasId}>
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.ALIAS_NAME')}
              </label>
              <NewInput
                id={aliasId}
                type="text"
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_ALIAS_NAME')}
                value={access.aliasName}
                onChange={(event) => onAccessChange?.({ aliasName: event.target.value })}
              />
            </div>
          )}
          <FieldSetupPanelContext.Provider value={shownPanel}>
            <LibraryTitleAppearanceContext.Provider value="inline">{children}</LibraryTitleAppearanceContext.Provider>
          </FieldSetupPanelContext.Provider>
          {showSetupExtras && access && access.toggles.length > 0 && <div className="cm-field-setup__access">{access.toggles.map(renderToggle)}</div>}
        </div>
      </div>
      {onWidthChange && (
        <div className="cm-field-setup__width">
          <div className="cm-field-setup__width-control">
            <label className="cm-field-setup__width-label" htmlFor={sliderId}>
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.WIDTH')}
            </label>
            <input
              id={sliderId}
              type="range"
              className="cm-field-setup__slider"
              min={MIN_WIDTH_PCT}
              max={MAX_WIDTH_PCT}
              value={widthPct}
              onChange={handleWidthChange}
              aria-invalid={!!widthError}
              aria-valuetext={`${widthPct}%`}
            />
          </div>
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
    </div>
  );
}

function parseWidthPct(value: string | undefined): number {
  const match = /^(\d+(?:\.\d+)?)\s*%?$/.exec((value ?? '').trim());
  return match ? Math.round(Number(match[1])) : 0;
}

export default FieldSetupCard;

const SETUP_CLOSE_ICON_SIZE = 20;

const MIN_WIDTH_PCT = 0;

const MAX_WIDTH_PCT = 100;

const FIELD_TYPE_LABEL_KEYS: Record<FieldSetupCardProps['fieldType'], string> = {
  dropdown: 'COMMON.FIELD_TYPES.DROPDOWN',
  text: 'COMMON.FIELD_TYPES.TEXT_BOX',
  radio: 'COMMON.FIELD_TYPES.RADIO_BUTTON',
  checkbox: 'COMMON.FIELD_TYPES.CHECKBOX',
  date: 'COMMON.FIELD_TYPES.DATE_PICKER',
  comment: 'COMMON.FIELD_TYPES.COMMENT',
  image: 'COMMON.FIELD_TYPES.IMAGE',
  status: 'COMMON.FIELD_TYPES.STATUS',
  source: 'COMMON.FIELD_TYPES.SOURCE',
  calculation: 'COMMON.FIELD_TYPES.CALCULATION',
  canvas: 'COMMON.FIELD_TYPES.CANVAS',
  time: 'COMMON.FIELD_TYPES.TIME_PICKER',
};

const OPTIONS_PANELS: FieldSetupPanel[] = ['setup', 'options'];

const PANELS_BY_TYPE: Record<FieldSetupCardProps['fieldType'], FieldSetupPanel[]> = {
  dropdown: OPTIONS_PANELS,
  text: OPTIONS_PANELS,
  radio: OPTIONS_PANELS,
  checkbox: OPTIONS_PANELS,
  date: OPTIONS_PANELS,
  comment: OPTIONS_PANELS,
  image: OPTIONS_PANELS,
  status: OPTIONS_PANELS,
  canvas: ['setup'],
  time: OPTIONS_PANELS,
  source: [],
  calculation: [],
};

const PANEL_LABEL_KEYS: Record<FieldSetupPanel, string> = {
  setup: 'CUSTOM_MODULE.SYSTEM_CANVAS.SETUP',
  options: 'CUSTOM_MODULE.SYSTEM_CANVAS.OPTIONS',
};

const TITLELESS_FIELD_TYPES: ReadonlyArray<FieldSetupCardProps['fieldType']> = ['canvas'];

const TOGGLE_LABEL_KEYS: Record<FieldSetupToggleKey, string> = {
  required: 'CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED',
  hasSupplierToggle: 'CUSTOM_MODULE.SYSTEM_CANVAS.SUPPLIER_TOGGLE',
  isVisibleToSupplier: 'CUSTOM_MODULE.SYSTEM_CANVAS.VISIBLE_TO_SUPPLIER_QUESTION',
  isEditableBySupplier: 'CUSTOM_MODULE.SYSTEM_CANVAS.EDITABLE_BY_SUPPLIER_QUESTION',
  showCanvasTitleInput: 'CUSTOM_MODULE.SYSTEM_CANVAS.CANVAS_TITLE_INPUT',
  showCanvasNotes: 'CUSTOM_MODULE.SYSTEM_CANVAS.NOTES_CONTROLS',
};

/**
 * One on/off row of the setup card (Figma radio toggle): a radio that flips its own state on click.
 * @param label - Text beside the radio.
 * @param checked - Whether the setting is on.
 * @param onToggle - Called when the user flips the setting.
 * @param disabled - Whether the row is locked.
 * @returns The toggle row.
 */
function SetupToggle({ label, checked, onToggle, disabled = false }: { label: string; checked: boolean; onToggle: () => void; disabled?: boolean }) {
  return (
    <NewRadio
      label={label}
      checked={checked}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onToggle();
      }}
      onChange={() => {}}
    />
  );
}
